import React, { useEffect, useRef, useState } from 'react';
import { X as CloseIcon, MoreVertical as MoreVertIcon } from 'lucide-react';

import {
    classificationNamingOptions,
    columnOrderOptions,
    sortOptions,
    TaxonomicCompositionFiltersState,
    updateFilters,
    updateFiltersDraft,
} from 'src/app/ecology-hub/state/taxonomic-composition/taxonomicCompositionSlice';
import { useAppDispatch, useAppSelector } from 'src/store';
import { getCurrentTaxonomicCompositionUIState } from 'src/app/ecology-hub/state/taxonomic-composition/taxonomicCompositionSelector';
import Select from 'src/shared/components/select/Select';
import { InputLabel } from 'src/shared/components/ui/input-label';
import { Separator } from 'src/shared/components/ui/separator';
import { PrimaryButton, SecondaryButton } from 'src/shared/components/button/Button';
import { Popover, PopoverTrigger, PopoverContent } from 'src/shared/components/ui/popover';
import styles from './TaxonomicCompositionFilters.module.scss';
import TaxonomicCompositionInlineFilters from '../taxonomic-composition-inline-filters/TaxonomicCompositionInlineFilters';

const FILTER_WIDTH = '100%';

const TaxonomicCompositionFilters = () => {
    const [open, setOpen] = useState<boolean>(false);
    const closeReasonRef = useRef<'apply' | 'cancel' | null>(null);

    const dispatch = useAppDispatch();
    const uiState = useAppSelector(getCurrentTaxonomicCompositionUIState);
    const {
        filtersDraft: { hierarchy, sortColumnsBy, classificationNaming },
    } = uiState;
    const onFiltersChange = (fragment: Partial<TaxonomicCompositionFiltersState>) => {
        dispatch(updateFiltersDraft(fragment));
    };

    useEffect(() => {
        dispatch(updateFiltersDraft({ ...uiState.filters }));
    }, []);

    const onApply = () => {
        dispatch(updateFilters({ ...uiState.filters, ...uiState.filtersDraft, highlightedBranchNodeId: null, visibleBranchNodeId: null }));
        closeReasonRef.current = 'apply';
        setOpen(false);
    };

    const closeWithoutApplying = () => {
        dispatch(updateFiltersDraft({ ...uiState.filters }));
        closeReasonRef.current = 'cancel';
        setOpen(false);
    };

    const handleOpenChange = (nextOpen: boolean) => {
        if (nextOpen) {
            setOpen(true);
            return;
        }

        if (closeReasonRef.current === 'apply') {
            closeReasonRef.current = null;
            setOpen(false);
            return;
        }

        if (closeReasonRef.current === 'cancel') {
            closeReasonRef.current = null;
            setOpen(false);
            return;
        }

        closeWithoutApplying();
    };

    return (
        <div className={styles.filtersContainer}>
            <TaxonomicCompositionInlineFilters />

            <Popover open={open} onOpenChange={handleOpenChange}>
                <PopoverTrigger asChild>
                    <SecondaryButton aria-describedby='filters-popover'>
                        <MoreVertIcon />
                    </SecondaryButton>
                </PopoverTrigger>
                <PopoverContent id='filters-popover' align='end' side='bottom' className='border-none bg-transparent p-0 shadow-none'>
                    <div className={styles.filters}>
                        <button type='button' className={styles.closeButton} onClick={closeWithoutApplying}>
                            <CloseIcon />
                        </button>
                        <div className={styles.filterTitle}>DISPLAY</div>

                        <div className={styles.selectInput}>
                            <InputLabel className={styles.filterLabel}>Classification naming</InputLabel>
                            <Select
                                isDisabled
                                width={FILTER_WIDTH}
                                options={classificationNamingOptions}
                                onChange={value => onFiltersChange({ classificationNaming: value })}
                                selectedValue={classificationNaming}
                            />
                        </div>
                        <Separator />

                        <div>SORT</div>
                        <div className={styles.selectInput}>
                            <InputLabel className={styles.filterLabel}>Sort columns by</InputLabel>
                            <Select
                                width={FILTER_WIDTH}
                                options={columnOrderOptions}
                                onChange={value => onFiltersChange({ hierarchy: value })}
                                selectedValue={hierarchy}
                            />
                        </div>

                        <div className={styles.selectInput}>
                            <InputLabel className={styles.filterLabel}>Sort rows by</InputLabel>
                            <Select
                                width={FILTER_WIDTH}
                                options={sortOptions}
                                onChange={value => onFiltersChange({ sortColumnsBy: value })}
                                selectedValue={sortColumnsBy}
                            />
                        </div>

                        <PrimaryButton onClick={onApply}>Apply</PrimaryButton>
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    );
};

export default TaxonomicCompositionFilters;
